import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Send, Trash2, X } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import type {
  EnvironmentView,
  ReleaseApproval,
  ReleaseDetail,
} from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  InlineAlert,
  LoadingState,
  StatusBadge,
} from "../../shared/ui";
import { ApprovalDecisionDrawer } from "./ApprovalDecisionDrawer";
import { AssignApproverDrawer } from "./AssignApproverDrawer";
import { useReleaseMutation } from "./useReleaseMutation";
import {
  APPROVAL_STATUS_LABELS,
  APPROVAL_STATUS_TONE,
  formatTimestamp,
} from "./releaseVocabulary";

/**
 * §31's approvals: who has to say yes, and whether they have.
 *
 * <p>Waiting and refused are drawn differently on purpose. One means somebody has not looked yet
 * and should be chased; the other means somebody looked and said no, and something has to change.
 * Collapsing them into "not approved" sends the reader to the wrong person.
 */
export function ReleaseApprovalsTab({
  release,
  leg,
  onChanged,
}: {
  release: ReleaseDetail;
  /**
   * The leg being worked on (§63). Sign-off belongs to a destination: agreeing that 2.1 may
   * reach a sandbox is not agreeing it may reach customers, so production is asked separately.
   */
  leg?: EnvironmentView | null;
  onChanged: () => void;
}) {
  const { can, user } = useAuth();
  const [assigning, setAssigning] = useState<ReleaseApproval | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<{
    approval: ReleaseApproval;
    decision: "APPROVED" | "REJECTED";
  } | null>(null);
  const { run, saving } = useReleaseMutation(release.id);

  const approvals = useQuery({
    queryKey: ["release-approvals", release.id],
    queryFn: () => api.get<ReleaseApproval[]>(`/api/v1/releases/${release.id}/approvals`),
  });

  /* Only the seats that count here: this environment's, plus the release-wide ones given
     before anybody was asked which destination they meant. */
  const rows = (approvals.data ?? []).filter(
    (row) => !leg || row.environment == null || row.environment.id === leg.id,
  );
  const outstanding = rows.filter((row) => row.mandatory && row.status !== "APPROVED").length;
  /*
    Why requesting might send nothing. Two causes, and both leave the button looking broken
    unless the page says so: a seat with nobody named has no address to send to, and nobody is
    ever mailed about their own action — so a release manager who is also the named approver
    gets silence, correctly.
  */
  const unassigned = rows.filter((row) => row.approver == null);
  const waitingOnMe = rows.filter(
    (row) => row.status === "WAITING" && row.approver?.id === user?.employeeId,
  );
  const wouldReach = rows.filter(
    (row) =>
      row.status === "WAITING"
      && row.approver != null
      && row.approver.id !== user?.employeeId,
  );

  const removeSeat = async (approvalId: string) => {
    setRemoving(approvalId);
    try {
      await api.del(`/api/v1/releases/${release.id}/approvals/${approvalId}`);
      onChanged();
    } finally {
      setRemoving(null);
    }
  };

  const requestAll = () =>
    void run(
      () =>
        api.post(
          `/api/v1/releases/${release.id}/approvals/request`
            + (leg ? `?environmentId=${leg.id}` : ""),
        ),
      onChanged,
    );

  return (
    <Card>
      <CardHeader
        title={leg ? `Approvals — ${leg.name}` : "Approvals"}
        subtitle={
          rows.length === 0
            ? "Nobody has been asked yet."
            : outstanding === 0
              ? "Everyone required has approved."
              : `${outstanding} required ${outstanding === 1 ? "approval" : "approvals"} outstanding`
        }
        action={
          can("RELEASE_EDIT") && (
            <Button
              size="sm"
              variant={rows.length === 0 ? "primary" : "secondary"}
              icon={<Send className="h-4 w-4" />}
              loading={saving}
              onClick={requestAll}
            >
              {rows.length === 0 ? "Request approvals" : "Request again"}
            </Button>
          )
        }
      />
      <CardBody className="space-y-3">
        {unassigned.length > 0 && (
          <InlineAlert tone="warning">
            {unassigned.length === 1
              ? `The ${unassigned[0]?.roleLabel ?? "approval"} seat has nobody assigned`
              : `${unassigned.length} seats have nobody assigned`}
            , so requesting sends no mail for {unassigned.length === 1 ? "it" : "them"}. Assign
            somebody and request again.
          </InlineAlert>
        )}

        {unassigned.length === 0 && wouldReach.length === 0 && waitingOnMe.length > 0 && (
          <InlineAlert tone="info">
            {waitingOnMe.length === 1
              ? "The one seat still waiting is yours"
              : `All ${waitingOnMe.length} seats still waiting are yours`}
            , and nobody is emailed about their own request. Requesting again will send nothing —
            these are yours to approve.
          </InlineAlert>
        )}

        {approvals.isPending ? (
          <LoadingState label="Loading approvals…" />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No approvals requested"
            message={`A ${release.releaseType.toLowerCase()} release normally needs sign-off from a few named people. Requesting them creates a seat for each.`}
            action={
              can("RELEASE_EDIT") ? (
                <Button onClick={requestAll}>Request the usual approvals</Button>
              ) : undefined
            }
          />
        ) : (
          <ul className="divide-y divide-ink-100">
            {rows.map((row) => {
              /* Only the named approver may answer, and the server enforces it. Hiding the
                 buttons from everyone else stops people discovering that by being refused. */
              const isMine =
                row.approver == null || row.approver.id === user?.employeeId;
              const canAnswer =
                can("RELEASE_APPROVE") && row.status === "WAITING" && isMine;

              return (
                <li key={row.id} className="flex items-start justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink-900">
                      {row.roleLabel}
                      {!row.mandatory && (
                        <span className="ml-2 text-xs font-normal text-ink-500">optional</span>
                      )}
                    </p>
                    <p className="mt-0.5 text-xs text-ink-500">
                      {row.approver?.fullName ?? "Nobody named yet"}
                      {row.respondedAt && ` · ${formatTimestamp(row.respondedAt)}`}
                    </p>
                    {row.comment && (
                      <p className="mt-1 rounded-md border-l-2 border-ink-200 bg-ink-50 px-2 py-1 text-xs text-ink-700">
                        {row.comment}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <StatusBadge tone={APPROVAL_STATUS_TONE[row.status]}>
                      {APPROVAL_STATUS_LABELS[row.status]}
                    </StatusBadge>
                    {can("RELEASE_EDIT") && row.status === "WAITING" && (
                      <Button variant="ghost" size="sm" onClick={() => setAssigning(row)}>
                        {row.approver ? "Reassign" : "Assign"}
                      </Button>
                    )}
                    {/* A seat this destination does not need. The defaults are a starting point,
                        not a policy: production might want only the project manager where the
                        sandbox wanted four. Only while it is still waiting — removing a seat
                        somebody has already answered would erase their decision. */}
                    {can("RELEASE_EDIT") && row.status === "WAITING" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Trash2 className="h-4 w-4" />}
                        aria-label={`Remove the ${row.roleLabel} seat`}
                        title="This destination does not need this sign-off"
                        loading={removing === row.id}
                        onClick={() => void removeSeat(row.id)}
                      />
                    )}
                    {canAnswer && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<X className="h-4 w-4" />}
                          onClick={() => setDeciding({ approval: row, decision: "REJECTED" })}
                        >
                          Refuse
                        </Button>
                        <Button
                          size="sm"
                          icon={<Check className="h-4 w-4" />}
                          onClick={() => setDeciding({ approval: row, decision: "APPROVED" })}
                        >
                          Approve
                        </Button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>

      <AssignApproverDrawer
        release={release}
        approval={assigning}
        onClose={() => setAssigning(null)}
        onSaved={onChanged}
      />
      <ApprovalDecisionDrawer
        release={release}
        approval={deciding?.approval ?? null}
        decision={deciding?.decision ?? "APPROVED"}
        onClose={() => setDeciding(null)}
        onSaved={onChanged}
      />
    </Card>
  );
}
